import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { NavigationHeader } from '../shared/NavigationHeader';
import { AdvancedSearch, SearchFilters } from '../search/AdvancedSearch';
import { Footer } from './Footer';
import { supabase } from '../../lib/supabase';
import { adminService } from '../../lib/admin';

interface AuthenticatedLayoutProps {
  user: any;
}

export function AuthenticatedLayout({ user }: AuthenticatedLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [activeFilters, setActiveFilters] = useState<SearchFilters | null>(null);
  const [savedFilters, setSavedFilters] = useState<SearchFilters>({
    query: '',
    tags: [],
    categories: [],
    subcategories: [],
    brands: []
  });
  
  // Determine current view based on route
  const getCurrentView = () => {
    const path = location.pathname;
    if (path.startsWith('/profile')) return 'profile';
    if (path === '/curations') return 'curations'; // Curated issues listing page
    if (path === '/discussions' || path.startsWith('/discussions/')) return 'discussions'; // Discussions page
    if (path.startsWith('/issues/')) return 'curations'; // Individual issue pages
    if (path.startsWith('/brand/')) return 'brands'; // Individual brand pages
    if (path.startsWith('/product/') && location.state?.from === 'curations') return 'curations'; // Product from curated context
    if (path.startsWith('/product/') && location.state?.from === 'brands') return 'brands'; // Product from brands context
    if (path === '/brands' || location.state?.view === 'brands') return 'brands';
    if (path === '/contribute' || location.state?.view === 'contribute') return 'contribute';
    if (path === '/' && location.state?.view === 'curations') return 'curations';
    if (path === '/' && location.state?.view === 'discover') return 'discover';
    if (path === '/') return 'discover';
    return 'discover';
  };

  const [currentView, setCurrentView] = useState<'curations' | 'discover' | 'discussions' | 'contribute' | 'brands' | 'profile' | 'my-curations'>(getCurrentView() as any);

  useEffect(() => {
    // Fetch user profile and admin status
    const loadUserData = async () => {
      if (user) {
        const [profileData, adminStatus] = await Promise.all([
          supabase.from('users').select('*').eq('id', user.id).single(),
          adminService.checkAdminStatus(user.id)
        ]);
        
        if (profileData.data) {
          setUserProfile(profileData.data);
        }
        setIsAdmin(adminStatus);
      }
    };
    
    loadUserData();
  }, [user]);

  // Update current view when location changes
  useEffect(() => {
    setCurrentView(getCurrentView());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.state]);

  const handleViewChange = (view: 'curations' | 'discover' | 'discussions' | 'contribute' | 'brands' | 'profile' | 'my-curations') => {
    setCurrentView(view);

    // Close advanced search when leaving search-enabled views
    if (view !== 'discover' && view !== 'curations') {
      setShowAdvancedSearch(false);
    }

    // Clear filters when switching between community and curated (different filter scopes)
    if (
      (currentView === 'discover' && view === 'curations') ||
      (currentView === 'curations' && view === 'discover')
    ) {
      handleClearFilters();
      setShowAdvancedSearch(false);
    }

    // Navigate to appropriate route while preserving SPA behavior
    switch(view) {
      case 'profile':
      case 'my-curations':
        if (userProfile?.username) {
          navigate(`/profile/${userProfile.username}`);
        } else if (user?.email) {
          const username = user.email.split('@')[0];
          navigate(`/profile/${username}`);
        }
        break;
      case 'discussions':
        navigate('/discussions', { state: { arrivedFromFlyover: true } });
        break;
      case 'brands':
      case 'contribute':
      case 'curations':
      case 'discover':
        // All go to home with different state
        navigate('/', { state: { view } });
        break;
    }
  };

  const handleSearchToggle = () => {
    // Toggle advanced search panel
    setShowAdvancedSearch(!showAdvancedSearch);
  };
  
  const handleAdvancedSearch = (filters: SearchFilters) => {
    setSavedFilters(filters);
    // Check if filters are empty (clear all was clicked)
    const hasFilters = filters.query || filters.tags.length > 0 ||
                       filters.categories.length > 0 || filters.subcategories.length > 0 ||
                       filters.brands.length > 0;
    setActiveFilters(hasFilters ? filters : null);
  };

  const handleClearFilters = () => {
    setSavedFilters({
      query: '',
      tags: [],
      categories: [],
      subcategories: [],
      brands: []
    });
    setActiveFilters(null);
  };

  return (
    <div className={`bg-[#F2F0E5] ${currentView === 'discussions' ? 'h-[100dvh] flex flex-col overflow-hidden' : 'min-h-screen'}`}>
      <NavigationHeader
        currentView={currentView}
        onViewChange={handleViewChange}
        user={user}
        userProfile={userProfile}
        isAdmin={isAdmin}
        showSearch={showAdvancedSearch}
        onSearchToggle={handleSearchToggle}
        hasActiveFilters={!!activeFilters}
      />
      {/* Advanced Search Panel */}
      <AdvancedSearch
        isOpen={showAdvancedSearch}
        onClose={() => setShowAdvancedSearch(false)}
        onSearch={handleAdvancedSearch}
        onClearFilters={handleClearFilters}
        currentFilters={savedFilters}
        mode={currentView === 'curations' ? 'curations' : 'discover'}
      />
      <Outlet context={{ user, userProfile, isAdmin, currentView, activeFilters, onClearFilters: handleClearFilters }} />
      {currentView !== 'discussions' && <Footer />}
    </div>
  );
}